import { useCallback, useEffect, useRef, useState } from "react";
import lottoApi from "./api";
import { useAuth } from "../contexts/JWTContext";

/*
 * แถบเตือน "ติดเทิร์น" — ตรรกะฝั่งลูกค้า (ใช้ร่วมกันทั้ง landing และ landing-ver2; หน้าตาแยกกันที่ LottoApp.js)
 *   • ถามเซิร์ฟเวอร์ /lotto/turn-status (รวมโบนัสการตลาดทุกแหล่งที่ยังล็อกอยู่ + ยอดแทงสะสมตั้งแต่ตอนรับ)
 *   • โหลดใหม่เมื่อยอดเครดิตเปลี่ยน (แทง/ได้รางวัล/รับโบนัส) หรือเปลี่ยนหน้า — แต่ไม่ถี่เกิน MIN_GAP_MS
 *   • ไม่ล็อกอิน / ไม่มีอะไรค้าง / เครดิตเหลือ 0 → ไม่แสดงแถบ (เซิร์ฟเวอร์ตอบ active=false)
 */

const MIN_GAP_MS = 2500;
const REFRESH_MS = 60000;

const nf = (n) => Number(n || 0).toLocaleString("th-TH", { maximumFractionDigits: 2 });
export const baht = (n) => `฿${nf(n)}`;

/** ชื่อก้อนโบนัสที่แสดง เช่น "เครดิตฟรี · ยูสใหม่" — ชื่อที่ขึ้นต้นด้วยประเภทอยู่แล้ว (เช่น "คืนยอดเสีย 2026-10-29") ไม่ซ้ำประเภทซ้อน */
export function itemTitle(item) {
  const label = item?.label || "โบนัส";
  const name = String(item?.name || "").trim();
  if (!name || name === label) return label;
  return name.startsWith(label) ? name : `${label} · ${name}`;
}

/** ข้อความหัวแถบ — pure */
export function turnHeadline(st) {
  if (!st || !st.active) return null;
  if (st.pending_count > 0) {
    return {
      title: "คุณติดเทิร์นโบนัส",
      detail: `แทงเพิ่มอีก ${baht(st.remaining)} จึงจะถอนโบนัสได้`,
      locked: st.bonus_locked,
      withdrawable: st.withdrawable,
    };
  }
  return {
    title: "คุณติดเทิร์นโปรโมชั่นฝาก",
    detail: "ทำเทิร์นให้ครบตามเงื่อนไขโปรโมชั่นก่อนถอน",
    locked: st.bonus_locked,
    withdrawable: st.withdrawable,
  };
}

/** ความคืบหน้ารวมของก้อนที่ยังค้าง (0–100) — pure */
export function overallPct(st) {
  const pending = (st?.items || []).filter((i) => i.remaining > 0);
  if (!pending.length) return 100;
  const req = pending.reduce((t, i) => t + i.required, 0);
  const done = pending.reduce((t, i) => t + i.done, 0);
  return req > 0 ? Math.max(0, Math.min(100, Math.round((done / req) * 100))) : 0;
}

/**
 * @param {*} depKey ค่าที่เปลี่ยนแล้วควรโหลดใหม่ (ยอดเครดิต + path)
 * @returns {{status:object|null, reload:Function}}
 */
export function useTurnStatus(depKey) {
  const { user, isAuthenticated } = useAuth();
  const uid = isAuthenticated ? user?.id : null;
  const [status, setStatus] = useState(null);
  const alive = useRef(true);
  const last = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
    };
  }, []);

  const load = useCallback(() => {
    if (!uid) {
      setStatus(null);
      return Promise.resolve();
    }
    last.current = Date.now();
    return Promise.resolve()
      .then(() => lottoApi.turnStatus())
      .then((d) => alive.current && setStatus(d && d.active ? d : null))
      .catch(() => {});
  }, [uid]);

  // เปลี่ยนยอด/หน้า → โหลดใหม่ แต่ไม่ถี่กว่า MIN_GAP_MS (ยอดเปลี่ยนรัวๆ ตอนแทงต่อเนื่อง)
  useEffect(() => {
    clearTimeout(timer.current);
    const wait = Math.max(0, MIN_GAP_MS - (Date.now() - last.current));
    timer.current = setTimeout(load, wait);
    return () => clearTimeout(timer.current);
  }, [load, depKey]);

  useEffect(() => {
    if (!uid) return undefined;
    const id = setInterval(() => !document.hidden && load(), REFRESH_MS);
    const onVis = () => !document.hidden && load();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [uid, load]);

  return { status, reload: load };
}
